// =======================================
// DATA TYPES
// =======================================

/**
 * Signup form data structure for new team members
 */
export interface SignupFormData {
  firstName: string;
  lastName: string;
  type: TeamMemberType;
  password: string;
  confirmPassword: string;
}

/**
 * Team member type options from database enum
 */
export type TeamMemberType = 'Engineer' | 'Draftsperson';

/**
 * Signup flow state tracking
 */
export interface SignupState {
  step: 'verifying' | 'form' | 'processing' | 'complete' | 'error';
  error?: string;
  isLoading: boolean;
}

// =======================================
// CONSTANTS AND OPTIONS
// =======================================

/**
 * Team member type options for dropdown
 */
export const TEAM_MEMBER_TYPE_OPTIONS = [
  { value: 'Engineer', label: 'Engineer' },
  { value: 'Draftsperson', label: 'Draftsperson' }
] as const;

/**
 * Form validation constants
 */
export const VALIDATION_RULES = {
  FIRST_NAME: {
    REQUIRED: 'First name is required',
    MIN_LENGTH: 'First name must be at least 2 characters',
    MAX_LENGTH: 'First name must be no more than 50 characters'
  },
  LAST_NAME: {
    REQUIRED: 'Last name is required',
    MIN_LENGTH: 'Last name must be at least 2 characters',
    MAX_LENGTH: 'Last name must be no more than 50 characters'
  },
  TYPE: {
    REQUIRED: 'Team member type is required'
  }
  // Password rules live in utils/passwordValidation (validateNewPassword)
} as const;

/**
 * Authentication-related routes
 */
export const AUTH_ROUTES = {
  LOGIN: '/login',
  TIMESHEETS: '/timesheets',
  PROJECTS: '/projects'
} as const;

/**
 * Default form state
 */
export const DEFAULT_SIGNUP_FORM_DATA: SignupFormData = {
  firstName: '',
  lastName: '',
  type: 'Engineer',
  password: '',
  confirmPassword: ''
} as const;

/**
 * Default signup state
 */
export const DEFAULT_SIGNUP_STATE: SignupState = {
  step: 'verifying',
  isLoading: false
} as const;

// =======================================
// COMPONENT SETTINGS
// =======================================


/**
 * Form field constraints
 */
export const FIELD_CONSTRAINTS = {
  FIRST_NAME: {
    MIN_LENGTH: 2,
    MAX_LENGTH: 50
  },
  LAST_NAME: {
    MIN_LENGTH: 2,
    MAX_LENGTH: 50
  }
} as const;

/**
 * Signup completion messages
 */
export const SUCCESS_MESSAGES = {
  ACCOUNT_CREATED: 'Your account has been created successfully!',
  PROFILE_COMPLETE: 'Your profile has been set up. You can now access the system.',
  REDIRECT_MESSAGE: 'You will be redirected to the timesheets page shortly.'
} as const;

/**
 * Error messages for signup flow
 */
export const ERROR_MESSAGES = {
  SIGNUP_FAILED: 'Failed to create your account. Please try again.',
  PROFILE_CREATION_FAILED: 'Account created but profile setup failed. Please contact support.',
  GENERIC_ERROR: 'An unexpected error occurred. Please try again.'
} as const; 